"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import {
  clearEditFlowSession,
  getQuoteReviewSummaryEditSection,
  setQuoteReviewSummaryEditSection,
} from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import withApplication from "@/components/with-application";
import { RenewalPage } from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import useResetStateTravelerEdit from "@/hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";

import {
  SectionHeader,
  SubsectionTitle,
  InfoRow,
  Divider,
  EditButton,
} from "./quote-summary-components";

type QuoteSummaryProps = {
  quoteSummaryTitle?: string;
  application?: string;
};

const getPolicyTypeName = (policyType: string): string => {
  switch (policyType) {
    case SINGLE_TRIP:
      return "Single Trip";
    case ANNUAL_MULTI_TRIP:
      return "Annual Multi Trip";
    default:
      return policyType;
  }
};

const getPolicyPackageName = (
  policyPackage: string,
  isCruiseValue: boolean
): string => {
  const packageNames: { [key: string]: string } = {
    BASIC: "Basic",
    COMPREHENSIVE: "Comprehensive",
    SIGNATURE: "Signature",
    ESSENTIALS: "Essentials",
    CLASSIC: "Classic" + (isCruiseValue ? " Cruise" : ""),
    DELUXE: "Deluxe" + (isCruiseValue ? " Cruise" : ""),
  };
  return packageNames[policyPackage] || policyPackage;
};

function QuoteSummary({
  quoteSummaryTitle = "Summary",
  application,
}: Readonly<QuoteSummaryProps>) {
  const quote = useSelector((state: RootState) => state.quote);
  const router = useRouter();
  const { current } = useWizard();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useWizardTranslation();
  const [expandedCoverDetails, setExpandedCoverDetails] =
    useState<boolean>(true);
  const [expandedOrganiserDetails, setExpandedOrganiserDetails] =
    useState<boolean>(false);
  const [expandedTravellers, setExpandedTravellers] = useState<{
    [key: number]: boolean;
  }>({});

  // Refs for scrolling to sections
  const coverDetailsSectionRef = useRef<HTMLDivElement>(null);
  const organiserDetailsSectionRef = useRef<HTMLDivElement>(null);
  const travellerSectionRefs = useRef<{ [key: number]: HTMLDivElement | null }>(
    {}
  );

  // Ref to track if resetQuestionData has been called to prevent duplicate API calls
  const hasResetQuestionData = useRef(false);

  const policyType = quote?.attributes?.tripDetails?.policyType;
  const policyPackage = quote?.attributes?.tripDetails?.policyPackage;
  const toLocationCountryDetails =
    quote?.attributes?.tripDetails?.toLocationCountryDetails || [];
  const startDate = quote?.attributes?.tripDetails?.startDate;
  const policyEndDate = quote?.attributes?.tripDetails?.policyEndDate;
  const endDate = quote?.attributes?.tripDetails?.endDate;
  const goingOnCruise = quote?.attributes?.tripDetails?.goingOnCruise;
  const travellers = quote?.attributes?.travellerDetails?.traveller || [];
  const organizer = quote?.attributes?.organiser;
  const optionalExtras = quote?.attributes?.priceResult?.optionalExtras || [];
  const product = quote?.attributes?.tripDetails?.product;
  const isCruise = product === "CRUISE";

  // Filter out organiser from travellers list if they're traveling
  // to avoid duplicate display (organiser details dropdown + traveller dropdown)
  const filteredTravellers = organizer?.travelling
    ? travellers.filter((traveller) => traveller.sequence !== 1)
    : travellers;

  // Check sessionStorage on mount and expand the relevant section
  // This is used when user cancels editing from edit pages
  useEffect(() => {
    const handleEditSection = async () => {
      const editSection = getQuoteReviewSummaryEditSection();

      if (editSection) {
        // Expand the relevant section based on what was being edited
        if (editSection === "cover" || editSection === "extras") {
          // Reset question data first if dates are empty (only once)
          if (!hasResetQuestionData.current && (!startDate || !endDate)) {
            hasResetQuestionData.current = true;
            try {
              showLoading();
              await resetQuestionData();
            } finally {
              hideLoading();
            }
          }

          // Then expand and scroll after data is updated
          setExpandedCoverDetails(true);
          setExpandedOrganiserDetails(false);
          setExpandedTravellers({});

          // Scroll to cover details section after expansion
          setTimeout(() => {
            coverDetailsSectionRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 300);
        } else if (editSection === "organiser") {
          setExpandedCoverDetails(false);
          setExpandedOrganiserDetails(true);
          setExpandedTravellers({});

          // Scroll to organiser details section after expansion
          setTimeout(() => {
            organiserDetailsSectionRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 300);
        } else if (editSection.startsWith("traveller-")) {
          const travellerIndex = Number.parseInt(editSection.split("-")[1], 10);
          setExpandedCoverDetails(false);
          setExpandedOrganiserDetails(false);
          setExpandedTravellers({ [travellerIndex]: true });

          // Scroll to traveller section after expansion
          setTimeout(() => {
            travellerSectionRefs.current[travellerIndex]?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 300);
        }

        // Clear sessionStorage after use
        clearEditFlowSession();
      }
    };

    handleEditSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleCoverDetailsToggle = () => {
    const newState = !expandedCoverDetails;
    setExpandedCoverDetails(newState);
    if (newState) {
      setExpandedOrganiserDetails(false);
      setExpandedTravellers({});
    }
  };

  const handleOrganiserDetailsToggle = () => {
    const newState = !expandedOrganiserDetails;
    setExpandedOrganiserDetails(newState);
    if (newState) {
      setExpandedCoverDetails(false);
      setExpandedTravellers({});
    }
  };

  const toggleTravellerExpansion = (index: number) => {
    const newState = !expandedTravellers[index];
    if (newState) {
      setExpandedCoverDetails(false);
      setExpandedOrganiserDetails(false);
      setExpandedTravellers({ [index]: true });
    } else {
      setExpandedTravellers((prev) => ({
        ...prev,
        [index]: false,
      }));
    }
  };

  const getMedicalConditions = (traveller: any): string[] => {
    const condition = traveller?.medical?.condition || "";
    return condition
      .split("|")
      .filter((c: string) => c.trim() !== "")
      .map((c: string) => c.trim());
  };

  const getLocationDisplay = () => {
    if (policyType === SINGLE_TRIP && toLocationCountryDetails?.length) {
      return {
        label: "Destination",
        value: toLocationCountryDetails
          .map((location) => location?.attributes?.countryName)
          .filter(Boolean)
          .join(", "),
      };
    }
    if (policyType === ANNUAL_MULTI_TRIP) {
      const locationMap = {
        UK: t("uk_only", "UK only"),
        IE: t("ireland_only", "Ireland only"),
        ESP: t("spain_only", "Spain only"),
        EUROPE_LR: t("europe_with_exclusions", "Europe, with exclusions"),
        EUROPE_HR: t("europe", "Europe"),
        WORLD_LR: t("worldwide_with_exclusions", "Worldwide, with exclusions"),
        WORLD_HR: t("worldwide", "Worldwide"),
      };
      const area = quote?.attributes?.tripDetails?.area;
      const regionValue = area
        ? locationMap[area as keyof typeof locationMap]
        : "";
      return regionValue
        ? {
            label: "Region",
            value: regionValue,
          }
        : null;
    }
    return null;
  };

  const locationDisplay = getLocationDisplay();

  const getCruiseCoverText = () => {
    if (goingOnCruise === undefined) return null;
    return goingOnCruise ? "Included" : "Not Included";
  };

  const cruiseCoverText = getCruiseCoverText();

  const formatDateFullYear = (dateString: string | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  };

  const organizerText = organizer
    ? `${organizer.firstName || ""} ${organizer.lastName || ""}`
    : null;

  const organizerDateOfBirth = organizer?.dateOfBirth || null;
  const emailAddress = organizer?.email || null;
  const phoneNumber = organizer?.telephone || null;

  // Build address as array of lines
  const addressLines = [
    organizer?.firstLineOfAddress,
    organizer?.secondLineOfAddress,
    organizer?.city,
    organizer?.postCode,
  ].filter(Boolean) as string[];

  const address = addressLines.length > 0 ? addressLines : null;

  // Get organiser's medical conditions if they are also a traveller (sequence 1)
  const organiserAsTraveller =
    organizer?.travelling && travellers.length > 0
      ? travellers.find((traveller) => traveller.sequence === 1)
      : null;
  const organiserMedicalConditions = organiserAsTraveller
    ? getMedicalConditions(organiserAsTraveller)
    : [];

  return (
    <main className="QuoteSummary" data-testid="quote-summary-testid">
      <div className="font-body-font">
        <h2
          className={`font-header-font ${
            application === RenewalPage
              ? "text-s leading-[21px]"
              : "text-[23px] leading-[27.6px]"
          } md:text-[25px] md:leading-[27.5px] text-text-title mb-spacing-xs md:text-left`}
        >
          {quoteSummaryTitle}
        </h2>

        {policyType && policyPackage && (
          <div
            ref={coverDetailsSectionRef}
            className="mt-spacing-l"
            data-testid="cover-details-section"
          >
            <SectionHeader
              title="Cover details"
              isExpanded={expandedCoverDetails}
              onToggle={handleCoverDetailsToggle}
              titleTestId="cover-details-title"
              arrowTestId="cover-details-arrow"
            />

            {expandedCoverDetails && (
              <div
                className="mt-spacing-m"
                data-testid="cover-details-expanded"
              >
                <InfoRow
                  label="Cover type"
                  values={[
                    getPolicyTypeName(policyType),
                    getPolicyPackageName(policyPackage, isCruise),
                  ]}
                  testId="cover-type-row"
                />
                <Divider testId="cover-type-divider" />

                {startDate && (
                  <>
                    <InfoRow
                      label="Cover start date"
                      value={formatDateFullYear(startDate)}
                      testId="cover-start-date-row"
                    />
                    <Divider testId="cover-start-date-divider" />
                  </>
                )}

                {policyEndDate && (
                  <>
                    <InfoRow
                      label="Cover end date"
                      value={formatDateFullYear(policyEndDate)}
                      testId="cover-end-date-row"
                    />
                    <Divider testId="cover-end-date-divider" />
                  </>
                )}

                {locationDisplay && (
                  <>
                    <InfoRow
                      label={locationDisplay.label}
                      value={locationDisplay.value}
                      testId="travel-destination-section"
                      valueClassName={
                        policyType === SINGLE_TRIP
                          ? "pl-[4.5rem] md:pl-[13rem] lg:pl-[8rem] text-right"
                          : undefined
                      }
                      labelClassName={
                        policyType === SINGLE_TRIP ? "self-start" : undefined
                      }
                    />
                    <Divider testId="travel-destination-divider" />
                  </>
                )}

                {cruiseCoverText !== null && (
                  <>
                    <InfoRow
                      label="Cruise cover"
                      value={cruiseCoverText}
                      testId="cruise-cover-section"
                    />
                    <Divider testId="cruise-cover-divider" />
                  </>
                )}

                <EditButton
                  label="Edit cover"
                  onClick={() => {
                    setQuoteReviewSummaryEditSection("cover");
                    router.push(
                      current.edges?.coverTypeEdit ?? "/?coverEdit=true"
                    );
                  }}
                  testId="edit-cover-button"
                />

                {optionalExtras.some(
                  (extra: any) => extra?.price?.[0]?.selected === true
                ) && (
                  <>
                    <div
                      className="mt-spacing-m mb-spacing-xs"
                      data-testid="extras-title"
                    >
                      <SubsectionTitle>Extras</SubsectionTitle>
                    </div>

                    {optionalExtras.map((extra: any) => {
                      const priceDetail = extra?.price?.[0];
                      if (priceDetail?.selected === true) {
                        const extraId = extra?.id || priceDetail?.name;
                        return (
                          <div key={`extra-${extraId}`}>
                            <InfoRow
                              label={priceDetail?.name}
                              value="Added"
                              testId={`extra-${extraId}`}
                            />
                            <Divider testId={`extra-divider-${extraId}`} />
                          </div>
                        );
                      }
                      return null;
                    })}

                    <EditButton
                      label="Edit extras"
                      onClick={() => {
                        setQuoteReviewSummaryEditSection("extras");
                        router.push("/quote");
                      }}
                      testId="edit-extras-button"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className={`mt-spacing-l`}>
          {(organizerText || emailAddress || phoneNumber || address) && (
            <div
              ref={organiserDetailsSectionRef}
              className="mt-spacing-l"
              data-testid="organiser-details-section"
            >
              <SectionHeader
                title="Organiser details"
                isExpanded={expandedOrganiserDetails}
                onToggle={handleOrganiserDetailsToggle}
                titleTestId="organiser-details-title"
                arrowTestId="organiser-details-arrow"
              />

              {expandedOrganiserDetails && (
                <div
                  className="mt-spacing-m"
                  data-testid="organiser-details-expanded"
                >
                  {organizerText && (
                    <>
                      <InfoRow
                        label="Organizer"
                        value={organizerText}
                        testId="organizer-section"
                      />
                      <Divider testId="organizer-divider" />
                    </>
                  )}

                  {organizerDateOfBirth && (
                    <>
                      <InfoRow
                        label="Date of birth"
                        value={formatDateFullYear(organizerDateOfBirth)}
                        testId="organizer-dob-section"
                      />
                      <Divider testId="organizer-dob-divider" />
                    </>
                  )}

                  {emailAddress && (
                    <>
                      <InfoRow
                        label="Email"
                        value={emailAddress}
                        testId="email-address-section"
                      />
                      <Divider testId="email-address-divider" />
                    </>
                  )}

                  {phoneNumber && (
                    <>
                      <InfoRow
                        label="Contact number"
                        value={phoneNumber}
                        testId="phone-number-section"
                      />
                      <Divider testId="phone-number-divider" />
                    </>
                  )}

                  {address && (
                    <>
                      <InfoRow
                        label="Address"
                        values={address}
                        testId="address-section"
                      />
                      <Divider testId="address-divider" />
                    </>
                  )}

                  {organizer?.travelling && (
                    <>
                      <InfoRow
                        label="Medical Conditions"
                        value={
                          organiserMedicalConditions.length > 0
                            ? undefined
                            : "None"
                        }
                        values={
                          organiserMedicalConditions.length > 0
                            ? organiserMedicalConditions
                            : undefined
                        }
                        testId="organiser-medical-section"
                      />
                      <Divider testId="organiser-medical-divider" />
                    </>
                  )}

                  <EditButton
                    label="Edit Organiser"
                    onClick={() => {
                      setQuoteReviewSummaryEditSection("organiser");
                      const basePath =
                        current.edges?.organiserEdit ?? "/organiser";
                      router.push(`${basePath}?travellerEdit=true`);
                    }}
                    testId="edit-organiser-button"
                  />
                </div>
              )}
            </div>
          )}

          {filteredTravellers.length > 0 && (
            <div className="mt-spacing-l" data-testid="travellers-section">
              {filteredTravellers.map((traveller, index) => {
                const travellerName = `${traveller?.firstName || ""} ${
                  traveller?.lastName || ""
                }`.trim();
                const medicalConditions = getMedicalConditions(traveller);
                const travellerId =
                  traveller?.sequence ||
                  `${traveller?.firstName}-${traveller?.lastName}`;

                return (
                  <div
                    key={`traveller-${travellerId}`}
                    className={index > 0 ? "mt-spacing-l" : ""}
                    ref={(el) => {
                      travellerSectionRefs.current[index] = el;
                    }}
                  >
                    <SectionHeader
                      title={travellerName}
                      isExpanded={expandedTravellers[index]}
                      onToggle={() => toggleTravellerExpansion(index)}
                      titleTestId={`traveller-title-${index}`}
                      arrowTestId={`traveller-arrow-${index}`}
                    />

                    {expandedTravellers[index] && (
                      <div
                        className="mt-spacing-m"
                        data-testid={`traveller-expanded-${index}`}
                      >
                        {traveller?.dateOfBirth && (
                          <>
                            <InfoRow
                              label="Date of birth"
                              value={formatDateFullYear(traveller.dateOfBirth)}
                              testId={`traveller-dob-${index}`}
                            />
                            <Divider
                              testId={`traveller-dob-divider-${index}`}
                            />
                          </>
                        )}

                        <>
                          <InfoRow
                            label="Medical Conditions"
                            value={
                              medicalConditions.length > 0 ? undefined : "None"
                            }
                            values={
                              medicalConditions.length > 0
                                ? medicalConditions
                                : undefined
                            }
                            testId={`traveller-medical-${index}`}
                          />
                          <Divider
                            testId={`traveller-medical-divider-${index}`}
                          />
                        </>

                        <EditButton
                          label="Edit traveller"
                          onClick={() => {
                            setQuoteReviewSummaryEditSection(
                              `traveller-${index}`
                            );
                            const basePath =
                              current.edges?.organiserEdit ?? "/organiser";
                            router.push(`${basePath}?travellerEdit=true`);
                          }}
                          testId={`edit-traveller-button-${index}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default withApplication(QuoteSummary);
